<template>
  <div
    :class="type"
    class="c-chart"
  >
    <!--
        1. Rifle
        2. Shop
        3. Embass
        4. Computer
        5. Radioactive
        -->
    <div class="c-chart__inner">
      <div class="c-chart__group">
        <div
          v-for="(icons, key) in iconGroup"
          :key="key"
          :class="getStyleClasses(icons)"
          :style="{ left: getStylePosition(key)[0] + '%', bottom: getStylePosition(key)[1] + '%'}"
          class="c-chart__icon-group"
          @click="showIconsGroup(icons, key, $event)"
        >
          <!-- Icons popup expanded - group of icons -->
          <ul
            v-if="icons.length > 1 && clickedOutside === false && activeChart === type"
            v-show="activeIconGroup === key"
            class="c-chart__icons-popup"
          >
            <li
              v-for="(icon, id) in icons"
              :key="id"
              title="Click to show the description"
              @click="showHover(icon.name, 1, 'grouped')"
            >
              <icon-base
                :height="48"
                :width="48"
              >
                <component :is="icon.iconName" />
              </icon-base>
            </li>
          </ul>
          <!-- End Icons popup expanded - group of icons -->
          <!-- Main icons - those appear on the graph directly - no interaction required - can be one or multiple, contains lightbox and hoverbox -->
          <ul
            class="c-chart__icons"
          >
            <li
              v-for="(icon, id) in icons"
              :key="id"
              class="c-chart__icon-item"
              title="Click to show the description"
              @click="showHover(icon.name, icons.length, 'main')"
            >
              <icon-base
                :class="'c-chart__main-icon'"
              >
                <component :is="icon.iconName" />
              </icon-base>
              <!-- Lightbox -->
              <transition name="popup-fade">
                <div
                  v-show="isLightboxVisible === icon.name && lightboxClick === false"
                  v-if="type === 'static'"
                  class="c-lightbox"
                >
                  <div class="u-container">
                    <div
                      class="c-lightbox__inner js-lightbox"
                    >
                      <!-- Close Lightbox Button -->
                      <button
                        class="c-lightbox__close"
                        @click="hideLightbox()"
                      >
                        <icon-base
                          :width="12"
                          :height="12"
                          :view-box="'0 0 12 12'"
                          :icon-name="'close'"
                        >
                          <icon-close />
                        </icon-base>
                      </button>
                      <!-- Lightbox Icon -->
                      <div class="c-lightbox__icon-container">
                        <icon-base
                          :width="72"
                          :height="72"
                          :class="'c-lightbox__icon'"
                        >
                          <component :is="icon.iconName" />
                        </icon-base>
                      </div>
                      <!-- Lightbox Title -->
                      <h4 class="c-lightbox__title">{{ component.chart[icon.name] }}
                      </h4>
                      <!-- Lightbox Content -->
                      <div
                        class="c-lightbox__content c-content-component"
                        v-html="getLightboxContent(icon.name)"
                      ></div>
                    </div>
                  </div>
                </div>
              </transition>
              <!-- End Lightbox -->
              <!-- Start Hover Box -->
              <div
                v-show="activeHover === icon.name && clickedOutside === false && activeChart === type"
                class="c-chart__hover-box"
                @click="openLightbox(icon.name)"
              >
                {{ component.chart[icon.name] }}
                <!-- Open Lightbox button -->
                <button
                  v-if="type === 'static'"
                  class="c-chart__hover-box-link"
                >
                  Click to learn more.
                </button>
              </div>
              <!-- End Hover Box -->
            </li>
          </ul>
          <!-- End Main icons -->
        </div>
        <!-- Chart Keys Left Side -->
        <ul class="c-chart__keys c-chart__keys--left">
          <li>Low</li>
          <li>Medium</li>
          <li>High</li>
        </ul>
        <!-- Chart Keys Bottom -->
        <ul class="c-chart__keys c-chart__keys--bottom">
          <li>Limited</li>
          <li>Moderate</li>
          <li>Severe</li>
        </ul>
      </div>
    </div>

    <!-- Chart Legend Left Side -->
    <div class="c-chart__legend c-chart__legend--bottom">
      Consequences
    </div>
    <!-- Chart Legend Bottom -->
    <div class="c-chart__legend c-chart__legend--side">
      Likelihood *
    </div>
  </div>
</template>

<style lang="scss" src="@/assets/sass/components/chart.scss"></style>
<style lang="scss" src="@/assets/sass/components/lightbox.scss"></style>

<script>
import IconBase from './IconBase.vue';
import IconRifle from './icons/IconRifle.vue';
import IconShop from './icons/IconShop.vue';
import IconRadioactive from './icons/IconRadioactive.vue';
import IconEmbassy from './icons/IconEmbassy.vue';
import IconComputer from './icons/IconComputer.vue';
import IconClose from './icons/IconClose.vue';

// Import the json for this component.
const lungoConfig = require('../assets/content/country-lungo.json');
const oolongConfig = require('../assets/content/country-oolong.json');
const jsonConfig = require('../assets/content/results.json');

export default {
  components: {
    IconBase,
    IconRifle,
    IconShop,
    IconRadioactive,
    IconComputer,
    IconEmbassy,
    IconClose,
  },
  props: {
    type: {
      type: String,
      required: true,
    },
    component: {
      type: Object,
      required: true,
    },
    threatNumbers: {
      type: Array,
      default: () => [],
    },
    clickedOutside: {
      type: Boolean,
      required: true,
    },
    lightboxClick: {
      type: Boolean,
      required: true,
    },
    activeChart: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      json: jsonConfig,
      iconGroup: this.getIconGroup(),
      activeIconGroup: '',
      activeHover: '',
      isLightboxVisible: '',
      settings: {
        maxScrollbarLength: 60,
      },
    };
  },
  methods: {
    chartJson() {
      return jsonConfig;
    },
    countryJson() {
      if (this.$store.getters.country === 'lungo') {
        return lungoConfig;
      }
      if (this.$store.getters.country === 'oolong') {
        return oolongConfig;
      }
      return null;
    },
    // X position is the same as the consequence.
    getXPosition(severityNumber) {
      if (typeof this.$store.state.severities[`consequences${severityNumber}`] !== 'undefined') {
        return this.$store.state.severities[`consequences${severityNumber}`];
      }
      return false;
    },
    // Y position is the average of the threat and vulnerability.
    getYPosition(severityNumber) {
      if (typeof this.$store.state.severities[`threat${severityNumber}`] === 'undefined') {
        return false;
      }
      if (typeof this.$store.state.severities[`vulnerability${severityNumber}`] === 'undefined') {
        return false;
      }
      let threat = this.$store.state.severities[`threat${severityNumber}`];
      let vulnerability = this.$store.state.severities[`vulnerability${severityNumber}`];
      threat = parseInt(threat, 10);
      vulnerability = parseInt(vulnerability, 10);

      return ((threat + vulnerability) / 2);
    },
    getIconName(severityNumber) {
      let iconName = '';
      if (severityNumber === '1') {
        iconName = 'rifle';
      } else if (severityNumber === '2') {
        iconName = 'shop';
      } else if (severityNumber === '3') {
        iconName = 'embassy';
      } else if (severityNumber === '4') {
        iconName = 'computer';
      } else {
        iconName = 'radioactive';
      }
      return iconName;
    },
    getIconGroup() {
      if (this.type === 'static') {
        return this.getStaticIconGroup();
      }
      return this.getDynamicIconGroup();
    },
    getStaticIconGroup() {
      const countryJson = this.countryJson();
      return countryJson.staticResultsIconGroup;
    },
    getDynamicIconGroup() {
      let xPosition = 0;
      let yPosition = 0;
      let name = 0;

      const finalArray = {};
      for (let i = 0; i < this.threatNumbers.length; i += 1) {
        xPosition = Number(this.getXPosition(this.threatNumbers[i]));
        yPosition = Number(this.getYPosition(this.threatNumbers[i]));
        name = this.getIconName(this.threatNumbers[i]);
        // Ensure we have this position already.
        const positionKey = `${xPosition},${yPosition}`;
        if (typeof finalArray[positionKey] === 'undefined') {
          finalArray[positionKey] = [];
        }

        // Icon object.
        const iconObject = {
          name,
          iconName: `icon-${name}`,
          xPosition,
          yPosition,
        };

        finalArray[positionKey].push(iconObject);
      }
      return finalArray;
    },
    getStyleClasses(icons) {
      let styleClass = '';
      const iconNumber = icons.length;

      if (iconNumber > 1) {
        styleClass = 'c-chart__icon-group--multiple';
        if (iconNumber === 2) {
          styleClass = styleClass.concat(' c-chart__icon-group--two');
        } else if (iconNumber === 3) {
          styleClass = styleClass.concat(' c-chart__icon-group--three');
        } else if (iconNumber === 4) {
          styleClass = styleClass.concat(' c-chart__icon-group--four');
        } else if (iconNumber === 5) {
          styleClass = styleClass.concat(' c-chart__icon-group--five');
        }
      } else {
        styleClass = 'c-chart__icon-group--single';
      }
      return styleClass;
    },
    getStylePosition(position) {
      // SBTODO: proper calculations
      const coordinates = position.split(',');
      const x = (parseFloat(coordinates[0]) / 3 - 0.25) * 100;
      const y = (parseFloat(coordinates[1]) / 3 - 0.25) * 100;
      return [x, y];
    },
    getLightboxContent(iconName) {
      if (typeof this.countryJson().results[iconName] !== 'undefined') {
        return this.countryJson().results[iconName];
      }
      return false;
    },
    showHover(key, iconsLength, type) {
      // Hide the icons group if a different main icon was clicked.
      if (type === 'main') {
        this.hideIconsGroup();
      }
      if (iconsLength === 1) {
        // Set the Results page currently visible icon group.
        this.activeHover = key;

        // Set active chart.
        this.$emit('setActiveChart', this.type);
      }
    },
    hideHover() {
      this.activeHover = '';
    },
    openLightbox(key) {
      if (this.type === 'static') {
        this.isLightboxVisible = key;
        document.querySelector('body').classList.add('is-scroll-locked');
        this.$emit('resetLightboxClick');
      }
    },
    hideLightbox() {
      this.isLightboxVisible = '';
      document.querySelector('body').classList.remove('is-scroll-locked');
    },
    showIconsGroup(icons, key, event) {
      if (icons.length > 1 && event.target.closest('.c-chart__icons-popup') === null) {
        this.hideHover();

        // Reset the clicked outside tracker state.
        this.$emit('resetClickedOutside');

        // Set active chart.
        this.$emit('setActiveChart', this.type);

        // Set the Results page currently visible icon group.
        this.activeIconGroup = key;
      }
    },
    hideIconsGroup() {
      this.activeIconGroup = '';
    },
  },
};
</script>
